import {
  Heart,
  GraduationCap,
  Users,
  Award,
  Clock,
  Sparkles,
  CheckCircle2,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SITE, TRAINING_AREAS } from "@/lib/site";
import { CAREERS_PAGE } from "@/constants/testIds";

const BENEFITS = [
  {
    icon: GraduationCap,
    title: "Comprehensive training",
    body:
      "Paid induction plus specialist training in dementia, medication, moving & handling, end-of-life and more.",
  },
  {
    icon: Users,
    title: "Supportive team",
    body:
      "Dedicated runs and consistent rotas — you'll get to know your clients and feel part of a real team.",
  },
  {
    icon: Award,
    title: "Career progression",
    body:
      "Routes into senior carer, team leader and management roles, with ongoing CPD support.",
  },
  {
    icon: Clock,
    title: "Flexible hours",
    body:
      "Full-time, part-time and bank shifts — we work hard to build rotas that work around your life.",
  },
  {
    icon: Heart,
    title: "Rewarding work",
    body:
      "Make a tangible difference every shift, supporting people to live well in their own homes.",
  },
  {
    icon: Sparkles,
    title: "Purple tunic team",
    body:
      "Join a recognisable, respected team known across Doncaster and Leicester for warmth, professionalism and care.",
  },
];

export default function Careers() {
  return (
    <div data-testid={CAREERS_PAGE.root}>
      <PageHero
        eyebrow="Careers at Embrace"
        title={
          <>
            Make every visit{" "}
            <em className="not-italic text-brand">matter</em>.
          </>
        }
        intro="We're always looking for kind, dependable people to join our team of Care Workers across Doncaster and Leicester. No two days are the same — but every day, you change someone's life for the better."
        image={SITE.imagery.careers}
        imageAlt="Embrace Healthcare manager welcoming a new team member with a handshake"
        imageClassName="grayscale"
      />

      {/* BENEFITS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Why join us
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
              A place where carers feel{" "}
              <em className="not-italic text-brand">valued</em>.
            </h2>
            <p className="mt-5 text-lg text-brand-mute leading-relaxed">
              Whether you're new to care or an experienced Care Worker, we'll
              invest in your development and support you to deliver care you
              can be proud of.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => (
              <article
                key={b.title}
                className="bg-white border border-brand-line rounded-3xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-soft flex items-center justify-center mb-6">
                  <b.icon className="w-6 h-6 text-brand" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl mb-3 text-brand-ink">
                  {b.title}
                </h3>
                <p className="text-brand-mute leading-relaxed text-[15px]">
                  {b.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TRAINING */}
      <section className="py-20 lg:py-28 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Training & development
            </span>
            <h2 className="font-display text-3xl lg:text-5xl mt-5 leading-tight">
              You'll be trained to deliver{" "}
              <em className="not-italic text-brand">outstanding care</em>.
            </h2>
            <p className="mt-5 text-lg text-brand-mute leading-relaxed">
              All Care Workers complete a paid induction followed by ongoing
              specialist training. We support you to develop real expertise.
            </p>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TRAINING_AREAS.map((t) => (
              <li
                key={t.slug}
                data-testid={CAREERS_PAGE.trainingItem(t.slug)}
                className="flex items-center gap-3 bg-white border border-brand-line rounded-2xl px-5 py-4"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-brand">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="font-semibold text-brand-ink">{t.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid={CAREERS_PAGE.applyCta}
            className="relative bg-brand-deep text-white rounded-[2rem] p-10 lg:p-16 overflow-hidden"
          >
            <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-brand/40 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-brand-blush/30 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand-blush">
                  <span className="w-8 h-px bg-brand-blush" /> Apply today
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
                  Ready to join the{" "}
                  <em className="not-italic text-brand-blush">Embrace</em> team?
                </h2>
                <p className="mt-5 text-lg text-white/80 max-w-xl">
                  Get in touch with our recruitment team — we'd love to hear
                  from you. Full UK right-to-work required. DBS and references
                  will be taken up on appointment.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-deep px-7 py-4 text-base font-semibold hover:bg-brand-blush hover:text-white transition-all duration-300 hover:-translate-y-0.5 w-full lg:w-auto"
                >
                  <Phone className="w-4 h-4" /> Call {SITE.phone}
                </a>
                <a
                  href="mailto:apply@embracehealthcare.co.uk?subject=Care%20Worker%20Application"
                  data-testid="careers-email-recruitment"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 text-white px-7 py-4 text-base font-semibold hover:bg-white/10 transition w-full lg:w-auto"
                >
                  <Mail className="w-4 h-4" /> Email recruitment{" "}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
